import {
  type FC, useCallback, useEffect, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { type ModalProps } from '~/modal/modalType.enum';
import {
  boundaryTerritoryGroupMatchingChanged, extraBoundaryFillUpdated,
} from '~/sidebar/sidebarApps/mapTools/boundary/extraFills/boundaryExtraFills.actionCreators';
import type { BoundaryFillConfig } from '~/sidebar/sidebarApps/mapTools/boundary/extraFills/extraFills.types';
import {
  createBoundaryFillConfig, patchActiveFillIntoBTG,
} from '~/sidebar/sidebarApps/mapTools/boundary/extraFills/extraFills.utils';
import {
  useBoundaryGroupPermissions,
  useBoundaryGroupSelector,
  useBoundaryGroupsIsListingLoadingSelector,
} from '~/store/boundaryGroups/boundaryGroups.selectors';
import { useBoundaryTerritoryDetailsIsLoadingSelector } from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.selectors';
import { boundaryTerritoryGroupsUpdateRequest } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionCreators';
import {
  useBoundaryTerritoryGroupsIsLoadingSelector, useBoundaryTerritoryGroupsWithActiveExtraFillSelector,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import { type BoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import {
  BoundarySettingsComponent, BoundarySettingsLayout,
} from './boundarySettings.component';
import {
  type BoundarySettingsPropsVariantsArguments, DEFAULT_RANGES_COUNT,
  useBoundarySettingsPropsVariants,
} from './useBoundarySettingsPropsVariants';

export type BoundarySettingsContainerProps = ModalProps<{
  boundaryTerritoryGroupId: number;
}>;

const areMatchingsEqual = (a: BoundaryTerritoryGroup['matchings'], b: BoundaryTerritoryGroup['matchings']): boolean => {
  if (a.matchingType !== b.matchingType) {
    return false;
  }
  const columnsA = a.matchColumns.map(column => column.columnId);
  const columnsB = b.matchColumns.map(column => column.columnId);
  if (columnsA.length !== columnsB.length) {
    return false;
  }
  return columnsA.every((column, index) => column === columnsB[index]);
};

export const BoundarySettingsContainer: FC<BoundarySettingsContainerProps> = ({
  boundaryTerritoryGroupId,
  isOpen,
  onClose,
}) => {
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const isBoundaryTerritoryGroupLoading = useBoundaryTerritoryGroupsIsLoadingSelector();
  const isBoundaryGroupsListingLoading = useBoundaryGroupsIsListingLoadingSelector();
  const isBoundaryTerritoryDetailsLoading = useBoundaryTerritoryDetailsIsLoadingSelector();
  const btgInfos = useBoundaryTerritoryGroupsWithActiveExtraFillSelector();
  const [boundaryTerritoryGroupInfo, setBoundaryTerritoryGroupInfo] = useState(() => {
    const btgInfo = btgInfos.find(
      btgInfo => btgInfo.boundaryTerritoryGroup.boundaryTerritoryGroupId === boundaryTerritoryGroupId,
    );
    if (!btgInfo) {
      throw new Error(`[${__filename}] Could not find boundaryTerritoryGroupInfo for boundaryTerritoryGroupId='${boundaryTerritoryGroupId}'.`);
    }
    return btgInfo;
  });
  const originalBTG = boundaryTerritoryGroupInfo.boundaryTerritoryGroup;
  const [boundaryTerritoryGroup, setBoundaryTerritoryGroup] = useState(() =>
    patchActiveFillIntoBTG(boundaryTerritoryGroupInfo),
  );
  const [valueIsInitial, setValueIsInitial] = useState(true);

  const boundaryGroup = useBoundaryGroupSelector(boundaryTerritoryGroup.boundaryGroupId);
  const boundaryPermissions = useBoundaryGroupPermissions(boundaryTerritoryGroup.boundaryGroupId);

  const updateBoundaryTerritoryGroup = useCallback<BoundarySettingsPropsVariantsArguments['onBoundaryTerritoryGroupUpdate']>(
    (updated: BoundaryTerritoryGroup, options?: { updateIsReset?: boolean }) => {
      setValueIsInitial(!!options?.updateIsReset);
      setBoundaryTerritoryGroup(updated);
    }, [],
  );

  const boundaryTerritoryGroupProps = useBoundarySettingsPropsVariants({
    boundaryTerritoryGroup,
    onBoundaryTerritoryGroupUpdate: updateBoundaryTerritoryGroup,
    changeIsLoading: setIsLoading,
  });
  const boundaryTerritoryGroupPropsLayout = boundaryTerritoryGroupProps?.layout;
  const handleBoundaryGroupNameChange = boundaryTerritoryGroupPropsLayout === BoundarySettingsLayout.Group ?
    boundaryTerritoryGroupProps?.onBoundaryGroupNameChange :
    null;

  const initialBoundaryGroupName = boundaryTerritoryGroupProps?.layout === BoundarySettingsLayout.Group ?
    boundaryTerritoryGroupProps?.boundaryGroupName :
    '';
  const [boundaryGroupName, setBoundaryGroupName] = useState<string>(initialBoundaryGroupName);

  const hasMatchingChanged = !areMatchingsEqual(
    originalBTG.matchings,
    boundaryTerritoryGroup.matchings,
  );

  const submit = useCallback(() => {
    if (!boundaryTerritoryGroup) {
      return;
    }

    if (boundaryTerritoryGroupPropsLayout === BoundarySettingsLayout.Group && initialBoundaryGroupName !== boundaryGroupName) {
      handleBoundaryGroupNameChange?.(boundaryGroupName);
    }

    // If there's an active extra fill, update it with the current style settings
    if (boundaryTerritoryGroupInfo?.activeFillConfig) {
      const updatedFillConfig = createBoundaryFillConfig({
        ...boundaryTerritoryGroupInfo.activeFillConfig,
        style: boundaryTerritoryGroup.settings.style,
        rangesCount: boundaryTerritoryGroup.settings.rangesCount ?? DEFAULT_RANGES_COUNT,
        bucketType: boundaryTerritoryGroup.settings.bucketType,
      } as BoundaryFillConfig);

      if (updatedFillConfig) {
        dispatch(extraBoundaryFillUpdated({
          boundaryTerritoryGroupId: boundaryTerritoryGroup.boundaryTerritoryGroupId,
          boundaryFillConfig: updatedFillConfig,
        }));
      }
      if (hasMatchingChanged) {
        dispatch(boundaryTerritoryGroupMatchingChanged({
          boundaryTerritoryGroupId: boundaryTerritoryGroup.boundaryTerritoryGroupId,
        }));

        dispatch(boundaryTerritoryGroupsUpdateRequest({
          ...originalBTG,
          matchings: boundaryTerritoryGroup.matchings,
        }, { onDone: onClose }));
      }
      else {
        onClose();
      }
    }
    else {
      if (hasMatchingChanged) {
        dispatch(boundaryTerritoryGroupMatchingChanged({
          boundaryTerritoryGroupId: boundaryTerritoryGroup.boundaryTerritoryGroupId,
        }));
      }
      dispatch(boundaryTerritoryGroupsUpdateRequest(boundaryTerritoryGroup, { onDone: onClose }));
    }
  },
  [boundaryGroupName, boundaryTerritoryGroup, boundaryTerritoryGroupInfo.activeFillConfig,
    boundaryTerritoryGroupPropsLayout, dispatch, hasMatchingChanged, initialBoundaryGroupName,
    handleBoundaryGroupNameChange, onClose, originalBTG]);

  // update local boundary territory group instance if was changed in the store (might happen after concurrency error)
  useEffect(() => {
    const btgInfo = btgInfos.find(
      btgInfo => btgInfo.boundaryTerritoryGroup.boundaryTerritoryGroupId === boundaryTerritoryGroupId,
    );
    if (boundaryTerritoryGroup && btgInfo &&
      (boundaryTerritoryGroup.etag?.timestamp !== btgInfo.boundaryTerritoryGroup.etag?.timestamp)) {
      setBoundaryTerritoryGroupInfo(btgInfo);
      setBoundaryTerritoryGroup(patchActiveFillIntoBTG(btgInfo));
    }
  }, [boundaryTerritoryGroup, boundaryTerritoryGroupId, btgInfos]);

  if (!boundaryTerritoryGroup || !boundaryTerritoryGroupProps || !boundaryGroup) {
    return null;
  }
  const restOfBoundarySettingsComponentProps = {
    ...boundaryTerritoryGroupProps,
    onBoundaryGroupNameChange: boundaryPermissions.boundaryIsEditable ? setBoundaryGroupName : undefined,
  };

  return (
    <BoundarySettingsComponent
      boundaryGroup={boundaryGroup}
      boundaryTerritoryGroup={boundaryTerritoryGroup}
      boundaryPermissions={boundaryPermissions}
      valueIsInitial={valueIsInitial}
      isLimitedMode={!!boundaryTerritoryGroupInfo.activeFillConfig}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={submit}
      isLoading={isLoading || isBoundaryTerritoryGroupLoading || isBoundaryTerritoryDetailsLoading || isBoundaryGroupsListingLoading}
      onBoundaryTerritoryGroupChange={updateBoundaryTerritoryGroup}
      {...restOfBoundarySettingsComponentProps}
    />
  );
};
